<!-- Status filter menu — single-select: picking a status replaces the current one,
     and re-picking it (or the chip's X) clears back to every unit. -->
<mat-menu #filterMenu="matMenu" class="filter-menu">
  @for (option of filterOptions; track option.state) {
    <button mat-menu-item class="filter-option" (click)="selectFilter(option.state)">
      <span [ngStyle]="{'width':'8px','height':'8px','border-radius':'50%','background-color': option.color,'display':'inline-block','margin-right':'8px','flex-shrink':'0'}"></span>
      {{ option.state | translate }} ({{ option.count }})
      @if (selectedState === option.state) {
        <ion-icon name="checkmark-outline" style="margin-left: 8px;" />
      }
    </button>
  }
</mat-menu>

<!-- Page title + section tabs; the selected section renders its own header below,
     matching the Contacts page paradigm. -->
@if (windowService.isDesktopRes) {
  <tidy-desktop-header class="no-bottom-margin">
    <div class="wrappable-container leasing-header-row">
      <tidy-text [title]="'Leasing'" />
      <tidy-tabs
        [tabs]="leasingTabs"
        [activeTab]="selectedTab"
        (tabChange)="selectTab($event)" />
    </div>
    <div class="non-wrappable-right-aligned-container">
      <automations-preview-button
        [tag]="'leasing'" />
    </div>
  </tidy-desktop-header>
} @else {
  <tidy-tabs
    [tabs]="leasingTabs"
    [activeTab]="selectedTab"
    (tabChange)="selectTab($event)"
    style="display: block; margin-bottom: 16px;" />
}

@if (selectedTab === 'screenings') {
  <screenings-tab />
}

@if (selectedTab === 'leases') {
  <leases-content />
}

@if (selectedTab === 'properties') {

  @if (errorMessage) {
    <tidy-error-message [text]="errorMessage" />
  }

  @if (windowService.isDesktopRes) {
    <tidy-desktop-header [isLoading]="!loaded">
      <div class="wrappable-container">
        <tidy-text [header]="'Properties'" />
        <form [formGroup]="form" novalidate action="#">
          <tidy-search-input
            formControlName="search"
            [placeholder]="'Search units'"
            [icon]="'assets/img/search-header.svg'"
            class="header-search" />
        </form>
        @if (loaded && !isRealMode) {
          <tidy-small-button
            [text]="'Filter'"
            [image]="'assets/img/filter-white.svg'"
            [matMenuTriggerFor]="filterMenu" />
          @if (activeFilter) {
            <ion-chip class="filter-chip">
              <span [ngStyle]="{'width':'8px','height':'8px','border-radius':'50%','background-color': activeFilter.color,'display':'inline-block','margin-right':'6px','flex-shrink':'0'}"></span>
              <ion-label>{{ activeFilter.state | translate }}</ion-label>
              <ion-icon name="close-circle" (click)="clearFilter()" />
            </ion-chip>
          }
        }
      </div>
      <div class="non-wrappable-right-aligned-container">
      </div>
    </tidy-desktop-header>
  }

  @if (!loaded) {
    @for (i of [1,2,3,4,5]; track i) {
      <div style="width: 100%; margin-bottom: 6px;">
        <ion-skeleton-text animated="true" style="height: 56px; border-radius: 6px" />
      </div>
    }
  }

  @if (loaded) {

    @if (!windowService.isDesktopRes) {
      <div class="mobile-filter-row" style="margin-bottom: 10px;">
        <form [formGroup]="form" novalidate action="#" style="flex: 1;">
          <tidy-search-input
            formControlName="search"
            [placeholder]="'Search units'"
            [icon]="'assets/img/search-header.svg'"
            class="header-search" />
        </form>
        @if (!isRealMode) {
          <tidy-small-button
            class="mobile-filter-button"
            [iconOnly]="true"
            [image]="'assets/img/filter-white.svg'"
            [matMenuTriggerFor]="filterMenu" />
        }
      </div>
      @if (activeFilter && !isRealMode) {
        <div class="mobile-filter-chips" style="margin-bottom: 10px;">
          <ion-chip class="filter-chip">
            <span [ngStyle]="{'width':'8px','height':'8px','border-radius':'50%','background-color': activeFilter.color,'display':'inline-block','margin-right':'6px','flex-shrink':'0'}"></span>
            <ion-label>{{ activeFilter.state | translate }}</ion-label>
            <ion-icon name="close-circle" (click)="clearFilter()" />
          </ion-chip>
        </div>
      }
    }

    <!-- Desktop table -->
    @if (windowService.isDesktopRes) {
      @if (shownRows.length > 0) {
        <div class="desktop-flex-fill">
          <tidy-table
            #tidyTable
            [dataSource]="shownRows"
            [displayedColumns]="headers"
            [columnslabels]="headers"
            [allRowIsClickable]="true"
            [rowsToShow]="50"
            [showColumnLines]="false"
            (sortChange)="sortChanged($event)"
            (cellClick)="onTableColumnClick($event)"
            [blockSorting]="true" />
        </div>
      }
      @if (shownRows.length === 0) {
        <tidy-alert>
          <tidy-row>
            <tidy-text [body]="emptyStateMessage" />
          </tidy-row>
        </tidy-alert>
      }
    }

    <!-- Mobile cards -->
    @if (!windowService.isDesktopRes) {
      @if (!isRealMode) {
        @for (view of shownUnitViews; track view.unit.id) {
          <tidy-card>
            <tidy-card-button (action)="goToUnitDetail(view.unit)" class="arrow-to-top">
              <tidy-row [alignLastItemRight]="true">
                <tidy-text [header]="view.unit.property_name + ' · ' + view.unit.unit" [translate]="false" />
                <tidy-text
                  [body]="view.displayState"
                  [translate]="false"
                  [ngStyle]="{ color: view.displayStateColor }" />
              </tidy-row>
              <tidy-row class="vertical-align-center">
                <tidy-image [src]="'assets/svg/bed-outline.svg'" class="body-size" />
                <tidy-text
                  [helper]="(view.unit.bedrooms === 0 ? 'Studio' : view.unit.bedrooms + ' bd') + ' / ' + view.unit.bathrooms + ' ba · ' + view.unit.sqft + ' sqft · $' + view.unit.market_rent.toLocaleString('en-US') + '/mo'"
                  [translate]="false" />
              </tidy-row>
              <tidy-row class="vertical-align-center">
                <span [ngStyle]="{'width':'8px','height':'8px','border-radius':'50%','background-color': view.displayStateColor,'margin-right':'6px','flex-shrink':'0'}"></span>
                <tidy-text [helper]="view.isByRoom && view.occupancyLabel ? view.occupancyLabel : view.unit.status_summary" [translate]="false" />
              </tidy-row>
              @if (view.isByRoom) {
                <tidy-row class="vertical-align-center">
                  <tidy-image [src]="'assets/svg/layers-outline.svg'" class="body-size" />
                  <tidy-text [helper]="'By room'" [translate]="false" />
                </tidy-row>
              }
            </tidy-card-button>
          </tidy-card>
        }
        @if (shownUnitViews.length === 0) {
          <tidy-alert>
            <tidy-row>
              <tidy-text [body]="emptyStateMessage" />
            </tidy-row>
          </tidy-alert>
        }
      }
      @if (isRealMode) {
        @for (view of shownRealViews; track view.id) {
          <tidy-card>
            <tidy-card-button (action)="onRealRowClick()" class="arrow-to-top">
              <tidy-row>
                <tidy-text [header]="view.name" [translate]="false" />
              </tidy-row>
              <tidy-row class="vertical-align-center">
                <tidy-image [src]="'assets/svg/bed-outline.svg'" class="body-size" />
                <tidy-text [helper]="view.bedBath" [translate]="false" />
              </tidy-row>
            </tidy-card-button>
          </tidy-card>
        }
        @if (shownRealViews.length === 0) {
          <tidy-alert>
            <tidy-row>
              <tidy-text [body]="emptyStateMessage" />
            </tidy-row>
          </tidy-alert>
        }
      }
    }
  }
}
